import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
} from 'firebase/auth';
import { auth, db } from './config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { demoAuthService, isDemoModeEnabled } from '../demo/demoRuntime';

const googleProvider = new GoogleAuthProvider();
const MAX_LOGIN_AUDIT_ENTRIES = 20;

const buildLoginAuditEvent = (method = 'password') => ({
  at: new Date().toISOString(),
  method,
});

const buildLimitedAuditTrail = (currentTrail, nextEvent) => {
  const safeTrail = Array.isArray(currentTrail) ? currentTrail.filter((entry) => entry && typeof entry === 'object') : [];
  return [...safeTrail, nextEvent].slice(-MAX_LOGIN_AUDIT_ENTRIES);
};

const realAuthService = {
  register: async (email, password, displayName) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, {
        displayName,
      });

      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email,
        name: displayName,
        roles: ['player'],
        role: 'player',
        team: '',
        createdAt: new Date(),
        lastLoginAt: null,
        loginAuditTrail: [],
        voiceEnabled: true,
        voiceVolume: 1,
        voiceRate: 1,
      });

      return userCredential.user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  login: async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  recordLoginAudit: async (user, method = 'password') => {
    if (!user?.uid) return;

    try {
      const event = buildLoginAuditEvent(method);
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email || '',
          name: user.displayName || '',
          roles: ['player'],
          role: 'player',
          team: '',
          createdAt: new Date(),
          voiceEnabled: true,
          voiceVolume: 1,
          voiceRate: 1,
          lastLoginAt: event.at,
          loginAuditTrail: [event],
        }, { merge: true });
        return;
      }

      const userData = userDoc.data() || {};
      const loginAuditTrail = buildLimitedAuditTrail(userData.loginAuditTrail, event);

      await setDoc(userRef, {
        lastLoginAt: event.at,
        loginAuditTrail,
        updatedAt: new Date(),
      }, { merge: true });
    } catch (error) {
      console.warn('Login audit trail update failed:', error);
    }
  },

  googleLogin: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          name: user.displayName || '',
          roles: ['player'],
          role: 'player',
          team: '',
          createdAt: new Date(),
          lastLoginAt: null,
          loginAuditTrail: [],
          voiceEnabled: true,
          voiceVolume: 1,
          voiceRate: 1,
        });
      }

      return user;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  getCurrentUser: () => auth.currentUser,
  onAuthStateChanged: (callback) => auth.onAuthStateChanged(callback),
};

export const authService = isDemoModeEnabled() ? demoAuthService : realAuthService;
